import React, { useState, useRef, useEffect } from 'react';
import './AIAssistant.css';
import { apiUrl } from '../config/apiBase';
import { useAuth } from '../AuthContext';

const AIAssistant = ({ currentQuestion, userAnswer }) => {
  const { refreshTokenIfNeeded } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Clean and format text by removing markdown symbols and organizing content
  const formatMessage = (text) => {
    if (!text) return '';
    
    return text
      // Remove bold markers (**text**)
      .replace(/\*\*(.+?)\*\*/g, '$1')
      // Remove italic markers (*text* or _text_)
      .replace(/\*(.+?)\*/g, '$1')
      .replace(/_(.+?)_/g, '$1')
      // Remove inline code markers (`text`)
      .replace(/`(.+?)`/g, '$1')
      // Add line breaks before numbered points (handles patterns like "sentence. 2." -> "sentence.\n\n2.")
      .replace(/\.\s+(\d+)\.\s+/g, '.\n\n$1. ')
      // Convert numbered lists at start to clean format with line breaks
      .replace(/^(\d+)\.\s+/gm, '\n$1. ')
      // Convert bullet points to clean format
      .replace(/[-•]\s+/g, '\n• ')
      // Remove extra spaces but preserve intentional spacing
      .replace(/ {2,}/g, ' ')
      // Clean up multiple line breaks (max 2)
      .replace(/\n{3,}/g, '\n\n')
      // Trim whitespace
      .trim();
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Initial greeting when chatbot opens
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([
        {
          type: 'assistant',
          content: "Hi! I'm your AI interview coach. I can help you improve your answers, suggest examples, explain the STAR method, or provide feedback on what you've written. What would you like help with?",
          timestamp: new Date()
        }
      ]);
    }
  }, [isOpen, messages.length]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMsg = {
      type: 'user',
      content: inputMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMsg]);
    setInputMessage('');
    setIsLoading(true);

    try {
      // Get auth token from Supabase session
      const token = await refreshTokenIfNeeded();
      
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      // Call API to get AI assistance
      const response = await fetch(apiUrl('/api/hr-screening/ai-assist'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          question: currentQuestion || 'General interview question',
          userAnswer: userAnswer || '',
          userQuery: inputMessage
        })
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();

      const assistantMsg = {
        type: 'assistant',
        content: formatMessage(data.suggestion || data.message || "I've analyzed your request. Here's my suggestion based on best practices."),
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMsg]);

    } catch (error) {
      console.error('AI Assistant error:', error);
      setMessages(prev => [...prev, {
        type: 'assistant',
        content: "I'm having trouble connecting right now. Make sure the backend server is running and try again.",
        timestamp: new Date()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickAction = async (action) => {
    const queries = {
      improve: 'How can I improve my answer using the STAR method?',
      examples: 'Can you give me an example of a strong interview answer?',
      structure: 'How should I structure my answer for maximum impact?'
    };
    
    const query = queries[action];
    if (!query) return;
    
    setInputMessage(queries[action] || '');
    
    // Auto-send the message
    const userMsg = {
      type: 'user',
      content: query,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMsg]);
    setIsLoading(true);

    try {
      // Get auth token from Supabase session
      const token = await refreshTokenIfNeeded();
      
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(apiUrl('/api/hr-screening/ai-assist'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          question: currentQuestion || 'General interview question',
          userAnswer: userAnswer || '',
          userQuery: query 
        })
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();

      setMessages(prev => [...prev, {
        type: 'assistant',
        content: formatMessage(data.suggestion || data.message || "Here's my advice on that topic."),
        timestamp: new Date()
      }]);

    } catch (err) {
      console.error('Quick action error:', err);
      setMessages(prev => [...prev, {
        type: 'assistant',
        content: "I'm having trouble right now. Please check that you're logged in and try again.",
        timestamp: new Date()
      }]);
    } finally {
      setIsLoading(false);
      setInputMessage('');
    }
  };

  if (!isOpen) {
    return (
      <button 
        className="ai-assistant-toggle" 
        onClick={() => setIsOpen(true)} 
        title="Open AI Assistant"
      >
        <span className="ai-icon">🤖</span>
        <span className="ai-label">AI Help</span>
      </button>
    );
  }

  return (
    <div className="ai-assistant-container">
      <div className="ai-assistant-header">
        <div className="ai-header-title">
          <span className="ai-icon">🤖</span>
          <span>AI Assistant</span>
        </div>
        <button className="ai-close-btn" onClick={() => setIsOpen(false)}>✕</button>
      </div>

      <div className="ai-assistant-messages">
        {messages.map((msg, index) => (
          <div key={index} className={`ai-message ${msg.type}`}>
            <div className="ai-message-content">{msg.content}</div>
            <div className="ai-message-time">
              {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="ai-message assistant">
            <div className="ai-message-content">
              <div className="ai-typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="ai-quick-actions">
        <button onClick={() => handleQuickAction('improve')}>✨ Improve</button>
        <button onClick={() => handleQuickAction('examples')}>💡 Example</button>
        <button onClick={() => handleQuickAction('structure')}>📝 Structure</button>
      </div>

      <div className="ai-assistant-input">
        <input
          type="text"
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
          placeholder="Ask me anything..."
          disabled={isLoading}
        />
        <button 
          onClick={handleSendMessage} 
          disabled={isLoading || !inputMessage.trim()}
          className="ai-send-btn"
        >
          ➤
        </button>
      </div>
    </div>
  );
};

export default AIAssistant;
